import type { ReactNode } from "react";
import { Card, ResponsiveGrid } from "@/design-system/components/ui";

/**
 * The money-and-velocity readouts, and the deck that holds all four families.
 *
 * <p>🔴 <b>Every figure carries its own denominator.</b> A percentage on its own
 * cannot say whether it came from three rows or three hundred, and the person
 * reading it is making a staffing or a follow-up decision. The dashboard's
 * conversion widget already states this rule; the tiles keep it.</p>
 */
export function Measure({
  label,
  value,
  detail,
}: {
  label: string;
  value: ReactNode;
  /** The denominator, the sample size, or what the figure is derived from. */
  detail?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="min-w-0 font-display text-h3 text-onyx tabular-nums">{value}</div>
      {detail && <div className="text-caption text-gray-mid">{detail}</div>}
    </div>
  );
}

export function MeasureTile({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} data-testid="measure-tile">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0 mb-space-2">
        {title}
      </h3>
      <div className="flex flex-wrap gap-x-space-5 gap-y-space-2">{children}</div>
    </section>
  );
}

/**
 * The tile deck.
 *
 * <p>🔴 <b>It sits BELOW the filter card and above the table</b> — the loans
 * list's placement. Tiles that respond to the filters belong under the control
 * that changes them, or the reader adjusts a filter and watches numbers move
 * somewhere above where they are looking.</p>
 *
 * <p>🔴 <b><c>ResponsiveGrid</c> reflows on the CONTAINER, never the
 * viewport.</b> A <c>md:grid-cols-2</c> deck is correct exactly once — on the
 * page it was authored against — and renders two 120px columns the first time
 * anything mounts it in a drawer or a split pane.</p>
 */
export function IndexTileDeck({ children }: { children: ReactNode }) {
  return (
    <Card className="mb-space-4" data-testid="index-tiles">
      <ResponsiveGrid cols={2} minColWidth="19rem" gap="md">
        {children}
      </ResponsiveGrid>
    </Card>
  );
}
